import {
  CarAxleType,
  CarAxleTypeLabels,
  CatalogObservationSource,
  CatalogObservationSourceLabels,
  CatalogServiceResolutionBasis,
  RimMaterialType,
  type CatalogServiceConditionRecord,
  type CatalogServiceValueRecord
} from '@navio/nsdk';
import { catalogConditionText } from './catalogFacts';
import { formatGaugeReading } from './gaugeValue';

export type ServiceSpecificationTexts = {
  title: string;
  pressure: string;
  torque: string;
  aluminium: string;
  steel: string;
  unspecified: string;
  empty: string;
  guidance: string;
  technicalGuidance: string;
  technicalDiagram: string;
  bodyAlternatives: string;
  bodyAlternativesDescription: string;
  axles: Record<CarAxleType, string>;
  /** Every source, so an observation from any of them names where it came from. */
  sources: Record<CatalogObservationSource, string>;
  resolutionBasis: Record<CatalogServiceResolutionBasis, string>;
  agreement: (evidenceCount: number, applicableCount: number) => string;
  overridden: string;
  /** Shown instead of a value when a group's specifications disagree. */
  multiple: string;
};

export const defaultServiceSpecificationTexts = {
  title: 'Wheel service specifications',
  pressure: 'Tire pressure',
  torque: 'Wheel nut torque',
  aluminium: 'Aluminium',
  steel: 'Steel',
  unspecified: 'Applicability not specified',
  empty: 'No catalog specification available',
  guidance:
    'Match the axle, tire size and rim material. Check the vehicle placard or the manufacturer instructions when values disagree.',
  technicalGuidance: 'Reviewed technical guidance',
  technicalDiagram: 'Technical diagram',
  bodyAlternatives: 'Generation body alternatives',
  bodyAlternativesDescription: 'Body styles for this generation. A specific body has not been matched to this car.',
  axles: { ...CarAxleTypeLabels },
  sources: {
    ...CatalogObservationSourceLabels,
    [CatalogObservationSource.REGISTRY]: 'Vehicle registry',
    [CatalogObservationSource.PLACARD]: 'Vehicle placard',
    [CatalogObservationSource.TECHNICIAN]: 'Worker',
    [CatalogObservationSource.CUSTOMER]: 'Customer preference'
  },
  resolutionBasis: {
    [CatalogServiceResolutionBasis.DIRECT]: 'Direct catalog value',
    [CatalogServiceResolutionBasis.EXACT_MODIFICATION]: 'Exact modification match',
    [CatalogServiceResolutionBasis.GENERATION_CONSENSUS]: 'All generation modifications agree',
    [CatalogServiceResolutionBasis.INSPECTED_TIRE_DIMENSION]: 'Matches the inspected tire size'
  },
  agreement: (evidenceCount, applicableCount) =>
    `${evidenceCount} of ${applicableCount} applicable modifications agree`,
  overridden: 'Manual override',
  multiple: 'Multiple specifications'
} satisfies ServiceSpecificationTexts;

// A staff override has no observation id, so the two id spaces are kept apart.
export const serviceSpecificationKey = (specification: CatalogServiceValueRecord): string =>
  specification.id !== null ? `observation-${specification.id}` : `override-${specification.overrideId}`;

export const serviceSpecificationLabel = (
  specification: CatalogServiceValueRecord,
  texts: ServiceSpecificationTexts,
  options?: { omitAxle?: boolean }
) => {
  const axle = options?.omitAxle || specification.axleType == null ? undefined : texts.axles[specification.axleType];
  const conditions = specification.conditions.flatMap((condition) => {
    const label = conditionText(condition, texts);
    return label == null ? [] : [label];
  });
  const parts = [axle, specification.tireSpecificationStr, ...conditions].filter(
    (part): part is string => part != null && part !== ''
  );
  return parts.join(' · ') || texts.unspecified;
};

// Pressures travel with their companion unit: "34 psi (2.3 bar)".
export const serviceSpecificationValue = (specification: CatalogServiceValueRecord) =>
  formatGaugeReading(specification);

export const serviceSpecificationProvenance = (
  specification: CatalogServiceValueRecord,
  texts: ServiceSpecificationTexts
) => {
  if (specification.overridden) {
    return texts.overridden;
  }
  const basis = texts.resolutionBasis[specification.resolutionBasis ?? CatalogServiceResolutionBasis.DIRECT];
  const evidenceCount = specification.evidenceModificationCount ?? 0;
  const applicableCount = specification.applicableModificationCount ?? 0;
  const agreement = applicableCount > 1 ? texts.agreement(evidenceCount, applicableCount) : undefined;
  return [basis, agreement, texts.sources[specification.source]].filter(Boolean).join(' · ');
};

// A worker reads only the rim material; every other qualifier code is dropped rather than printed
// as a machine name on a phone in a driveway.
const conditionText = (condition: CatalogServiceConditionRecord, texts: ServiceSpecificationTexts) =>
  catalogConditionText(condition, {
    rimMaterialTexts: { [RimMaterialType.ALUMINIUM]: texts.aluminium, [RimMaterialType.STEEL]: texts.steel },
    omitUnknown: true
  });
